import React, { useState, useEffect, useMemo } from "react";
import { sanitizeInput, validateCommentInput } from "../../utils/inputValidation";
import { calculateReviewPercentage } from "../../utils/reviewPercentageCalculation";

const SemesterReviewForm = ({
  template,
  onSubmit,
  onChange,
  initialAnswers = {},
  readOnly = false,
  language = "de",
  showBonusCalculation = false,
  placeholders = {},
  bonusStats: initialBonusStats, // Receive from backend, BUT prioritise local calc if answers change
}) => {
  const [answers, setAnswers] = useState(initialAnswers);
  const [submitted, setSubmitted] = useState(false);

  // Helper for Review Percentage Calculation
  // REAL-TIME: Recalculate whenever answers change
  const currentBonusStats = useMemo(() => {
    if (!showBonusCalculation) return { totalPoints: 0, maxPoints: 0, percentage: 0 };
    // Calculate review percentage for KOMPETENZEN template
    return calculateReviewPercentage(answers, template);
  }, [answers, template, showBonusCalculation]);

  // Use local calc for display if available, otherwise fallback to prop (e.g. initial load)
  // However, local calc is superior because it's instant.
  const displayBonusStats = currentBonusStats; 

  // Update answers when initialAnswers changes (after save/reload)
  useEffect(() => {
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  // Notify parent of answer changes
  useEffect(() => {
    if (onChange && Object.keys(answers).length > 0) {
      onChange(answers);
    }
  }, [answers, onChange]);

  const handleScaleChange = (itemCode, value) => {
    setAnswers((prev) => ({
      ...prev,
      [itemCode]: {
        ...prev[itemCode],
        scaleValue: value,
        comment: prev[itemCode]?.comment || "",
      },
    }));
  };

  const handleCommentChange = (itemCode, value) => {
    // Sanitize comment input to prevent XSS
    const sanitizedValue = sanitizeInput(value);

    setAnswers((prev) => ({
      ...prev,
      [itemCode]: {
        scaleValue: prev[itemCode]?.scaleValue || "",
        comment: sanitizedValue,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all comments before submission
    let hasInvalidInput = false;
    for (const [, answer] of Object.entries(answers)) {
      if (answer.comment) {
        const validation = validateCommentInput(answer.comment);
        if (!validation.isValid) {
          alert(
            `Comment contains a potentially unsafe script pattern. Please remove the corresponding text.`,
          );
          hasInvalidInput = true;
          break;
        }
      }
    }

    if (hasInvalidInput) {
      return;
    }

    setSubmitted(true);
    if (onSubmit) {
      onSubmit(answers);
    }
    setTimeout(() => setSubmitted(false), 3000);
  };

  // Loading / Missing template
  if (!template || !template.sections || template.sections.length === 0) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-circle-notch fa-spin text-sunrise-red text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Template is loading...
          </h2>
          <p className="text-gray-600">
            Please wait a moment while the Semester Review template is being
            prepared.
          </p>
          <p className="text-xs text-gray-400 mt-3">
            Debug: Template: {template ? "available" : "missing"} | Sections:{" "}
            {template?.sections ? template.sections.length : "none"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screens">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header-Card  
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start">
                <div className="w-14 h-14 rounded-2xl bg-sunrise-red/10 flex items-center justify-center mr-4">
                  <i className="fas fa-clipboard-check text-sunrise-red text-2xl"></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-grey-dusk mb-1">
                    Semester Review
                  </h1>
                  <p className="text-gray-600 text-sm md:text-base">
                    Evaluate the defined competencies and provide constructive,
                    comprehensible feedback.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        */}
        {/* Success-Alert */}
        {submitted && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-check-circle text-green-500 mr-3"></i>
                <span className="font-semibold">
                  Formular erfolgreich gespeichert!
                </span>
              </div>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="text-green-500 hover:text-green-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        <div className="card-brand overflow-hidden">
          <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white p-6 rounded-t-lg">
            <h2 className="text-xl font-bold flex items-center">
              <i className="fas fa-user-check mr-3"></i>
              {template.name || "Semesterreview Formular"}
            </h2>
            {template.description && (
              <p className="mt-2 text-sm text-white/90">
                {template.description}
              </p>
            )}
          </div>

          <div className="p-6 md:p-8">
            <form onSubmit={handleSubmit}>
              <div className="space-y-5 mb-6">
                {template.sections.map((section, sectionIndex) => (
                  <div
                    key={section.code}
                    className="border border-pale-dawn rounded-xl overflow-hidden bg-ice-white/40"
                  >
                    {/* Section Header */}
                    <div className="bg-ice-white border-b border-pale-dawn px-4 py-3">
                      <div className="flex items-center">
                        <span className="bg-sunrise-red text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mr-3">
                          {sectionIndex + 1}
                        </span>
                        <div>
                          <h3 className="text-base md:text-lg font-semibold text-grey-dusk">
                            {section.title}
                          </h3>
                          {section.subtitle && (
                            <p className="text-xs text-gray-500">
                              {section.subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section Body */}
                    <div className="p-4 md:p-5">
                      {section.intro && (
                        <div className="bg-blue-50 border-l-4 border-sunrise-red/70 p-3 mb-4 rounded-md">
                          <p className="text-xs md:text-sm text-gray-700">
                            {section.intro}
                          </p>
                        </div>
                      )}

                      {/* Items */}
                      {section.items && section.items.length > 0 ? (
                        section.items.map((item, itemIndex) => (
                          <div
                            key={item.code}
                            className={`py-4 ${
                              itemIndex < section.items.length - 1
                                ? "border-b border-pale-dawn/70"
                                : ""
                            }`}
                          >
                            {/* Frage */}
                            <label className="block font-semibold text-grey-dusk mb-1">
                              {item.label}
                            </label>
                            {item.description && (
                              <p className="text-xs md:text-sm text-gray-600 mb-3">
                                {item.description}
                              </p>
                            )}

                            {/* Skala */}
                            {item.scaleType === "RADIO" &&
                              item.scaleOptions && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {item.scaleOptions.map((option) => {
                                    const selected =
                                      answers[item.code]?.scaleValue === option;
                                    return (
                                      <label
                                        key={option}
                                        className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full border text-xs md:text-sm ${
                                          readOnly
                                            ? "cursor-default"
                                            : "cursor-pointer"
                                        } transition-all ${
                                          selected
                                            ? "bg-sunrise-red text-white border-sunrise-red shadow-sm"
                                            : "bg-white text-gray-700 border-pale-dawn hover:border-sunrise-red/70 hover:bg-ice-white"
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={item.code}
                                          value={option}
                                          checked={selected}
                                          onChange={(e) =>
                                            !readOnly &&
                                            handleScaleChange(
                                              item.code,
                                              e.target.value,
                                            )
                                          }
                                          disabled={readOnly}
                                          className="sr-only"
                                        />
                                        {option}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}

                            {/* Kommentar */}
                            <div>
                              <label
                                htmlFor={`comment-${item.code}`}
                                className="block text-xs md:text-sm font-medium text-grey-dusk mb-1"
                              >
                                {item.commentLabel ||
                                  (language === "en"
                                    ? "Remarks"
                                    : "Bemerkungen")}
                                {item.commentRequired && (
                                  <span className="text-red-600 ml-1">*</span>
                                )}
                                {(!readOnly && !answers[item.code]?.comment && placeholders[item.code]) && (
                                  <span className="ml-2 text-xs font-normal text-gray-400 italic">
                                    <i className="fas fa-history mr-1"></i>
                                    from prev. report
                                  </span>
                                )}
                              </label>
                              <textarea
                                id={`comment-${item.code}`}
                                rows="3"
                                className="input-brand w-full min-h-[96px] resize-vertical"
                                placeholder={
                                  readOnly
                                    ? ""
                                    : (!answers[item.code]?.comment && placeholders[item.code])
                                      ? placeholders[item.code]
                                      : language === "en"
                                        ? "Your remarks on the process, concrete observations and development opportunities..."
                                        : "Ihre Bemerkungen zum Verlauf, konkreten Beobachtungen und Entwicklungsmoeglichkeiten..."
                                }
                                value={answers[item.code]?.comment || ""}
                                onChange={(e) =>
                                  !readOnly &&
                                  handleCommentChange(item.code, e.target.value)
                                }
                                required={!readOnly && item.commentRequired}
                                disabled={readOnly}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 italic text-sm">
                          Keine Items in dieser Sektion hinterlegt.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit Button im Brand-Style */}
              {!readOnly && (
                <>
                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg bg-sunrise-red text-white font-semibold text-sm md:text-base hover:bg-sunrise-600 transition-colors shadow-brand focus:outline-none focus:ring-2 focus:ring-sunrise-red/60 focus:ring-offset-2"
                  >
                    <i className="fas fa-save mr-2"></i>
                    Save Form
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
        {/* Bonus Summary Card */}
        {showBonusCalculation && (
          <div className="card-brand mt-4 p-4 bg-white border-l-4 border-grey-dusk">
            <h3 className="text-lg font-bold text-grey-dusk mb-2">
              Bonusberechnung (Bildungsbericht)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded shadow-sm border border-grey-dusk">
                <p className="text-xs text-uppercase text-gray-500 font-semibold tracking-wider">
                  Punkte
                </p>
                <p className="text-2xl font-bold text-grey-dusk/50">
                  {displayBonusStats.totalPoints}{" "}
                  <span className="text-sm text-gray-400 font-normal">
                    / {displayBonusStats.maxPoints}
                  </span>
                </p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm border border-grey-dusk">
                <p className="text-xs text-uppercase text-gray-500 font-semibold tracking-wider">
                  Erreicht
                </p>
                <p className="text-2xl font-bold text-grey-dusk/50">
                  {/* Display percentage from front-end calc (can go up to 150%) */}
                  {(displayBonusStats.percentage || 0).toFixed(1)}%
                </p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm border border-grey-dusk flex items-center justify-center">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-grey-dusk/50 h-2.5 rounded-full"
                    style={{
                      // Scale to 150% maximum (128 points = 150% = full width)
                      width: `${Math.min(((displayBonusStats.percentage || 0) / 150) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SemesterReviewForm;
